/**
 * sessionReset — the ONE place a session ends.
 *
 * ─── WHY THIS FILE EXISTS ────────────────────────────────────────────────
 * Before this, the app had six different logout implementations. Four called
 * `localStorage.clear()` and hoped; two removed a hand-listed subset of keys
 * and missed several (`userId`, `authToken`, `sessionToken`, `tenant_id`,
 * `nextgen_builder_id`); one forgot to clear the React context user at all.
 * None of them told the server, so `users.session_token` stayed valid in the
 * database after the student had "logged out". And none of them touched React
 * state, in-flight requests, or the axios default headers held in module
 * memory for the life of the tab.
 *
 * That is why TeeGee still showed the previous user's messages: those messages
 * were React state in a component mounted at the app root that nothing ever
 * unmounted. Clearing localStorage could not remove them because they were
 * never in localStorage to begin with.
 *
 * So logout is not "delete some keys". It is five separate things, and this
 * module does all five in a fixed order:
 *
 *   1. bump the SESSION EPOCH, so any response already in flight for the old
 *      session is recognised as stale and discarded rather than landing in the
 *      new session's UI;
 *   2. tell the SERVER, so the session token stops being accepted;
 *   3. clear the axios default headers held in module memory;
 *   4. remove every user-scoped storage key, by explicit inventory and by
 *      prefix rule for the templated ones;
 *   5. broadcast `tsg:session-reset`, which is what makes components holding
 *      private state in memory drop it — and, for TeeGee, remount entirely.
 *
 * Order matters. The epoch is bumped FIRST so that a response arriving during
 * the server call is already stale. The broadcast is LAST so listeners observe
 * fully-cleared storage.
 *
 * ─── ADDING A NEW USER-SCOPED KEY ────────────────────────────────────────
 * Add it to USER_SCOPED_KEYS (exact) or USER_SCOPED_PREFIXES (templated).
 * A key that holds anything about a specific person belongs in one of them.
 * Preferences that are about the DEVICE rather than the person (theme, sidebar
 * width) are deliberately left alone — see PRESERVED_KEYS.
 */

import axios from 'axios';
import API_BASE_URL from '../Config/config';

/* ── 1. The session epoch ───────────────────────────────────────────────────
 * A monotonic counter, incremented on every session boundary. It lives in
 * module memory rather than storage because its only job is to invalidate work
 * started by THIS tab's JavaScript, and a fresh tab has nothing to invalidate.
 *
 * The pattern for callers is: capture the epoch before an async call, compare
 * after it resolves, drop the result if it changed. That closes the logout /
 * login race in brief section 17 — a slow request issued by user A cannot
 * paint into user B's session, even though its promise resolves after the
 * switch. AbortController alone does not close it, because a response can be
 * mid-parse when the abort fires.
 */
let sessionEpoch = 0;

export const getSessionEpoch = () => sessionEpoch;

/** True if the session has changed since `epoch` was captured. */
export const isStaleEpoch = (epoch) => epoch !== sessionEpoch;

export const bumpSessionEpoch = () => {
  sessionEpoch += 1;
  return sessionEpoch;
};

/* ── 2. Storage inventory ──────────────────────────────────────────────── */

/**
 * Exact keys that identify or describe a specific signed-in person.
 * Compiled from an audit of every localStorage call site in src/.
 */
export const USER_SCOPED_KEYS = Object.freeze([
  // identity / credentials
  'user', 'user_id', 'userId', 'user_pk', 'user_email', 'user_name', 'user_phone',
  'userType', 'token', 'authToken', 'auth_token', 'refresh_token',
  'session_token', 'sessionToken', 'skillgenie_session',
  'college_token', 'collegeUsername', 'college_name',
  'nextgen_builder_id', 'tenant_id', 'tenantInfo',
  // entitlement — must not survive into another account
  'subscription', 'subscription_data', 'pending_discount', 'pending_discount_amount',
  'collegeChallengeEnabled',
  // learning state that belongs to a person, not a device
  'enrollment', 'enrollment_id', 'currentEnrollmentId', 'enrollment_last',
  'levelCompleted', 'pendingLevelComplete', 'elite_specialization',
  'user_notes', 'user_activity', 'last_read_doc', 'selectedAvatar',
  'completed_docs_broken', 'completed_docs_python-premium',
  'mock_interview_usage_count', 'has_submitted_suggestion',
  // TeeGee's own guest-mode context. Cleared on every boundary so a visitor's
  // captured name/email never becomes the next person's greeting.
  'tsg_bot_ctx',
]);

/**
 * Prefixes for the templated, per-user keys (`progress_<userId>_<slug>`,
 * `llm_trial_<userId>`, …). Matching by prefix is what makes this robust:
 * we cannot enumerate keys for a user id we no longer have.
 */
export const USER_SCOPED_PREFIXES = Object.freeze([
  'progress_', 'mentor_', 'completed_docs_', 'enrollment_', 'tenant_enrollment_',
  'dailyActivity_', 'daily_activity_', 'streak_', 'visit_count_', 'quick_stats_',
  'timeTracking_', 'totalTasks_',
  'payment_confirmed_', 'subscription_data_', 'user_trial_started_',
  'user_trial_initialized_at_', 'llm_trial_', 'llm_trial_welcome_shown_',
  'user_created_date_',
  'tsg_used_', 'mock_interview_state_v1:', 'mock_interview_form_data_',
  'mock_interview_clicks_', 'login_session_start_',
  'chatbot_popup_session_', 'dashboard_popup_session_',
  'teegee_',
]);

/**
 * Device preferences, deliberately preserved. These describe the browser, not
 * the person, and wiping them makes logout feel like vandalism. Listed
 * explicitly so the choice is visible rather than accidental.
 */
export const PRESERVED_KEYS = Object.freeze([
  'theme', 'sidebarExpanded', 'preferred_language', 'ai_mentor_lang',
  'nexus_billing_currency', 'tsg_bot_seen',
]);

const isUserScoped = (key) => {
  if (PRESERVED_KEYS.includes(key)) return false;
  if (USER_SCOPED_KEYS.includes(key)) return true;
  return USER_SCOPED_PREFIXES.some((p) => key.startsWith(p));
};

/**
 * Remove every user-scoped key from both storages.
 * Keys are collected before deletion — mutating storage while iterating its
 * index shifts the remaining entries and silently skips some.
 */
export const clearUserScopedStorage = () => {
  const removed = [];
  [window.localStorage, window.sessionStorage].forEach((storage) => {
    if (!storage) return;
    let keys = [];
    try { keys = Object.keys(storage); } catch (e) { return; /* storage blocked */ }
    keys.filter(isUserScoped).forEach((k) => {
      try { storage.removeItem(k); removed.push(k); } catch (e) { /* quota/private mode */ }
    });
  });
  // sessionStorage's two scroll-popup flags are per-visit, not per-person, but
  // they are cheap to reset and doing so avoids a second visitor inheriting a
  // "already saw the popup" state that was not theirs.
  try {
    window.sessionStorage.removeItem('sg_form_shown_35');
    window.sessionStorage.removeItem('sg_form_shown_skillgenie');
  } catch (e) { /* ignore */ }
  return removed;
};

/* ── 3. Axios module state ───────────────────────────────────────────────
 * The app mutates the GLOBAL axios defaults (there is no axios.create()
 * anywhere), so these headers live in module memory for the life of the tab.
 * localStorage.clear() does not touch them, which is how a "logged out" tab
 * kept sending the previous user's bearer token.
 */
export const clearAxiosAuthHeaders = () => {
  try {
    delete axios.defaults.headers.common.Authorization;
    delete axios.defaults.headers.common['X-Session-Token'];
    delete axios.defaults.headers.common['x-builder-id'];
    delete axios.defaults.headers.common['user-id'];
  } catch (e) { /* nothing meaningful to do */ }
};

/* ── 4. Server-side invalidation ─────────────────────────────────────────── */

/**
 * Tell the server the session is over, so the token stops being accepted.
 *
 * Best-effort and time-boxed: a student on a bad connection must still be able
 * to log out of the browser in front of them. But this is attempted FIRST, and
 * with the token still present, because it cannot work afterwards.
 */
const notifyServerLogout = async (kind) => {
  const token = (() => { try { return window.localStorage.getItem('session_token'); } catch (e) { return null; } })();
  if (!token) return;
  const path = kind === 'college' ? '/colleges/logout' : '/user/logout';

  // AbortController + setTimeout rather than AbortSignal.timeout, which is
  // missing in Safari before 16 and in jsdom. The catch below would have
  // swallowed the resulting TypeError, so on those browsers the server logout
  // would silently never have been sent - the exact failure this whole change
  // exists to fix. Caught by the frontend test suite.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 4000);
  try {
    await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-Session-Token': token },
      body: JSON.stringify({ sessionToken: token }),
      signal: controller.signal,
    });
  } catch (e) {
    // Logging out locally is still correct and still an improvement; the token
    // is single-use-per-login and will be overwritten at the next sign-in.
    console.warn('[sessionReset] server logout could not be confirmed:', e && e.message);
  } finally {
    clearTimeout(timer);
  }
};

/* ── 5. The broadcast ────────────────────────────────────────────────────── */

export const SESSION_RESET_EVENT = 'tsg:session-reset';

/**
 * Components holding private state IN MEMORY listen for this. Storage clearing
 * cannot reach them; only they can drop their own state.
 *
 * TeeGee goes further than listening: the gate keys its subtree on the session
 * identity, so a change of user unmounts the panel outright and there is no
 * state left to leak, whether or not a listener behaved correctly.
 */
const broadcast = (reason) => {
  try {
    window.dispatchEvent(new CustomEvent(SESSION_RESET_EVENT, { detail: { reason, epoch: sessionEpoch } }));
  } catch (e) { /* very old browsers */ }
};

/* ── The public API ──────────────────────────────────────────────────────── */

/**
 * End the current session completely.
 *
 * @param {object}   options
 * @param {'college'|'user'} [options.kind]     which server logout endpoint
 * @param {boolean}  [options.notifyServer]     default true
 * @param {Function} [options.setUser]          the UserContext setter, so the
 *   in-memory context user is cleared too — several old logout paths forgot
 *   this and left a stale user object in React until the next full reload.
 * @returns {Promise<{epoch:number, removedKeys:string[]}>}
 */
export const resetSession = async ({ kind = 'user', notifyServer = true, setUser } = {}) => {
  // 1. Invalidate in-flight work FIRST, so anything resolving from here on is
  //    recognisably stale — including responses to the logout call itself.
  const epoch = bumpSessionEpoch();

  // 2. Server, while the token is still available to send.
  if (notifyServer) await notifyServerLogout(kind);

  // 3. Module-memory headers.
  clearAxiosAuthHeaders();

  // 4. Storage.
  const removedKeys = clearUserScopedStorage();

  // 5. React.
  if (typeof setUser === 'function') {
    try { setUser(null); } catch (e) { /* a bad setter must not block logout */ }
  }
  broadcast('logout');

  return { epoch, removedKeys };
};

/**
 * Called at the START of a successful login, BEFORE the new credentials are
 * written to storage.
 *
 * Why this is separate from resetSession: a login is not a logout. There is no
 * server call to make (the previous token may already be dead, and the new one
 * has just been issued), and no broadcast yet, because storage is about to be
 * repopulated and listeners would observe a half-built session.
 *
 * What it is for: the previous person's residue. If user A signed out badly —
 * or never signed out at all, because the tab was closed — their per-user keys
 * (`progress_alice_java`, `llm_trial_alice`, cached entitlements) are still
 * sitting in this browser. Writing B's credentials on top of them leaves B
 * reading A's cached learning state. Clearing first makes every login a clean
 * boundary regardless of how the previous session ended.
 *
 * Pair it with startSession() once the new credentials are in place.
 */
export const prepareForLogin = () => {
  bumpSessionEpoch();          // anything still in flight is now stale
  clearAxiosAuthHeaders();     // module memory, which storage clearing cannot reach
  return clearUserScopedStorage();
};

/**
 * A session STARTED. Bump the epoch and broadcast so anything holding state
 * from a previous session drops it now, rather than at the next logout.
 *
 * This is what makes "user A logs out, user B logs in" safe even if user A's
 * logout was somehow incomplete: B's login is itself a reset boundary.
 */
export const startSession = () => {
  const epoch = bumpSessionEpoch();
  broadcast('login');
  return epoch;
};

export default {
  resetSession,
  startSession,
  prepareForLogin,
  getSessionEpoch,
  isStaleEpoch,
  bumpSessionEpoch,
  clearUserScopedStorage,
  clearAxiosAuthHeaders,
  SESSION_RESET_EVENT,
  USER_SCOPED_KEYS,
  USER_SCOPED_PREFIXES,
  PRESERVED_KEYS,
};
